import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { IPaginatedReactQueryParams } from './interfaces';
import {
  Contact,
  ContactListOption,
  ContactRetentionOverviewSort,
  ContactsSort,
  ICreateContactParams,
  JobFunction,
} from '../api/contactsApi/interfaces';
import {
  createContact,
  deleteContact,
  getClientContactsList,
  getContact,
  getContactRetentionOverview,
  getContacts,
  getContactsList,
  getJobFunctionsList,
  updateContact,
} from '../api/contactsApi';
import { AxiosError } from 'axios';

export const useGetContacts = ({
  page,
  pageSize,
  sortField,
  search,
  filter,
}: IPaginatedReactQueryParams<ContactsSort>) => {
  return useQuery({
    queryKey: ['contacts', page, pageSize, sortField, search, filter],
    queryFn: () => {
      return getContacts(
        page + 1,
        pageSize,
        sortField,
        search || undefined,
        filter
      );
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useGetContact = (id: number) => {
  return useQuery<Contact>({
    queryFn: () => getContact(id),
    queryKey: ['contact', id],
    enabled: !!id,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if ((error as AxiosError)?.response?.status === 404) {
        return false;
      }

      return failureCount < 3;
    },
  });
};

export const useCreateContactMutation = () => {
  return useMutation<number, AxiosError, ICreateContactParams>({
    mutationFn: createContact,
  });
};

export const useUpdateContactMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    number,
    AxiosError,
    { contactId: number; contactData: ICreateContactParams }
  >({
    mutationFn: async ({ contactId, contactData }) =>
      updateContact(contactId, contactData),
    onSuccess: (contactId: number) => {
      queryClient.invalidateQueries({
        queryKey: ['contact', contactId],
      });
    },
  });
};

export const useDeleteContact = () => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, number>({
    mutationFn: deleteContact,
    onSuccess: (_, contactId) => {
      queryClient.removeQueries(['contact', contactId]);
    },
  });
};

export const useGetJobFunctionsList = (enabled: boolean = true) => {
  return useQuery<JobFunction[]>({
    queryFn: () => getJobFunctionsList(),
    queryKey: ['job-functions-list'],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled,
  });
};

export const useGetContactsList = (enabled: boolean = true) => {
  return useQuery<ContactListOption[]>({
    queryFn: () => getContactsList(),
    queryKey: ['contacts-list'],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled,
  });
};

export const useGetClientContactsList = (
  clientId: number | undefined,
  enabled: boolean = true
) => {
  return useQuery<ContactListOption[]>({
    queryFn: () => getClientContactsList(clientId || 0),
    queryKey: ['client-contacts-list', clientId],
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: enabled && clientId !== undefined,
  });
};

export const useGetContactRetentionOverview = ({
  page,
  pageSize,
  sortField,
  search,
  filter,
}: IPaginatedReactQueryParams<ContactRetentionOverviewSort>) => {
  return useQuery({
    queryKey: [
      'contact-retention-overview',
      page,
      pageSize,
      sortField,
      search,
      filter,
    ],
    queryFn: () => {
      return getContactRetentionOverview(
        page + 1,
        pageSize,
        sortField,
        search || undefined,
        filter
      );
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};
